import type { MetricCardInstance } from '@diagon-alley/common';

// =============================================================================
// SHARED EXTRACTION DEFINITIONS
// =============================================================================
// avgActualLeadTime and avgExpectedLeadTime appear in both strips.
// Defined once to avoid duplication across the two config arrays.

const avgActualLeadTimeExtraction = (callId: string) => ({
    name: 'avgActual',
    from: callId,
    path: 'data',
    perItem: {
        fn: 'DATE_DIFF' as const,
        args: [
            {
                fn: 'COALESCE' as const,
                args: ['item.deliveryDate', 'item.expiryDate'],
            },
            'item.orderDate',
        ],
    },
    aggregate: 'AVERAGE' as const,
});

const avgExpectedLeadTimeExtraction = (callId: string) => ({
    name: 'avgExpected',
    from: callId,
    path: 'data',
    field: 'expectedLeadTime',
    aggregate: 'AVERAGE' as const,
});

// =============================================================================
// SUPPLIERS LIST STRIP — 4 metric cards
// Shown at the top of the Suppliers page.
// =============================================================================

export const SUPPLIER_LIST_METRICS: MetricCardInstance[] = [
    // ── 1. Average Lead Time ─────────────────────────────────────────────────
    {
        id: 'suppliers-avg-lead-time',
        componentType: 'METRIC_CARD',
        apiCalls: [
            {
                id: 'pos',
                endpoint: '/purchase-orders/list',
                method: 'POST',
                params: {
                    type: 'INCOMING',
                    status: ['Received', 'Partially Received'],
                    fetchAll: true,
                },
            },
        ],
        extractions: [avgActualLeadTimeExtraction('pos')],
        fieldBindings: [
            { fieldName: 'label', value: { type: 'literal', value: 'Average Lead Time' } },
            { fieldName: 'value', value: { type: 'ref', name: 'avgActual' } },
            {
                fieldName: 'subtext',
                value: { type: 'literal', value: 'Calculated across all supply orders' },
            },
        ],
        format: { type: 'number', suffix: ' days', decimals: 1 },
        isEditable: false,
    },

    // ── 2. Lead Time Variance ────────────────────────────────────────────────
    {
        id: 'suppliers-lead-time-variance',
        componentType: 'METRIC_CARD',
        apiCalls: [
            {
                id: 'pos',
                endpoint: '/purchase-orders/list',
                method: 'POST',
                params: {
                    type: 'INCOMING',
                    status: ['Received', 'Partially Received'],
                    fetchAll: true,
                },
            },
        ],
        extractions: [
            avgActualLeadTimeExtraction('pos'),
            avgExpectedLeadTimeExtraction('pos'),
        ],
        fieldBindings: [
            { fieldName: 'label', value: { type: 'literal', value: 'Lead Time Variance' } },
            {
                fieldName: 'value',
                value: {
                    type: 'computed',
                    fn: 'PERCENTAGE_CHANGE',
                    args: [
                        { type: 'ref', name: 'avgActual' },
                        { type: 'ref', name: 'avgExpected' },
                    ],
                },
            },
            {
                fieldName: 'subtext',
                value: { type: 'literal', value: 'Computed across all received orders' },
            },
        ],
        format: { type: 'percentage', decimals: 1 },
        isEditable: false,
    },

    // ── 3. Fill Rate % ───────────────────────────────────────────────────────
    {
        id: 'suppliers-fill-rate',
        componentType: 'METRIC_CARD',
        apiCalls: [
            {
                id: 'pos',
                endpoint: '/purchase-orders/list',
                method: 'POST',
                params: {
                    type: 'INCOMING',
                    status: ['Received', 'Partially Received'],
                    fetchAll: true,
                },
            },
        ],
        extractions: [
            { name: 'numerator', from: 'pos', path: 'data', field: 'actualQuantity', aggregate: 'SUM' },
            { name: 'denominator', from: 'pos', path: 'data', field: 'totalQuantity', aggregate: 'SUM' },
        ],
        fieldBindings: [
            { fieldName: 'label', value: { type: 'literal', value: 'Fill Rate' } },
            {
                fieldName: 'value',
                value: {
                    type: 'computed',
                    fn: 'MULTIPLY',
                    args: [
                        {
                            type: 'computed',
                            fn: 'DIVIDE',
                            args: [
                                { type: 'ref', name: 'numerator' },
                                { type: 'ref', name: 'denominator' },
                            ],
                        },
                        { type: 'literal', value: 100 },
                    ],
                },
            },
        ],
        format: { type: 'number', decimals: 1, suffix: '%' },
        isEditable: false,
    },

    // ── 4. On-Time Delivery % ────────────────────────────────────────────────
    {
        id: 'suppliers-on-time-delivery',
        componentType: 'METRIC_CARD',
        apiCalls: [
            {
                id: 'pos',
                endpoint: '/purchase-orders/list',
                method: 'POST',
                params: {
                    type: 'INCOMING',
                    status: ['Received', 'Partially Received'],
                    fetchAll: true,
                },
            },
        ],
        extractions: [
            {
                // DATE_DIFF(benchmarkDate, actualDeliveryDate) >= 0 means on time or same day
                // benchmarkDate = plannedDate ?? (orderDate + expectedLeadTime)
                // Returns null when late → excluded from COUNT
                name: 'onTimeCount',
                from: 'pos',
                path: 'data',
                perItem: {
                    fn: 'DATE_DIFF',
                    args: ['item.benchmarkDate', 'item.actualDeliveryDate'],
                },
                aggregate: 'COUNT',
            },
            {
                name: 'totalCount',
                from: 'pos',
                path: 'data',
                field: 'purchaseOrderId',
                aggregate: 'COUNT',
            },
        ],
        fieldBindings: [
            { fieldName: 'label', value: { type: 'literal', value: 'On-Time Delivery' } },
            {
                fieldName: 'value',
                value: {
                    type: 'computed',
                    fn: 'MULTIPLY',
                    args: [
                        {
                            type: 'computed',
                            fn: 'DIVIDE',
                            args: [
                                { type: 'ref', name: 'onTimeCount' },
                                { type: 'ref', name: 'totalCount' },
                            ],
                        },
                        { type: 'literal', value: 100 },
                    ],
                },
            },
        ],
        format: { type: 'number', decimals: 1, suffix: '%' },
        isEditable: false,
    },
];

// =============================================================================
// VENDOR SIDE PANE — 4 metric cards
// Shown inside the vendor detail pane when a row is selected.
// All API calls include vendors:['{{vendorId}}'] — resolved from context at runtime.
// Pass context={{ vendorId: selectedVendorId }} to the MetricStrip.
// =============================================================================

export const VENDOR_PANE_METRICS: MetricCardInstance[] = [
    // ── 1. Average Lead Time (per vendor) ────────────────────────────────────
    {
        id: 'vendor-avg-lead-time',
        componentType: 'METRIC_CARD',
        apiCalls: [
            {
                id: 'pos',
                endpoint: '/purchase-orders/list',
                method: 'POST',
                params: {
                    type: 'INCOMING',
                    status: ['Received', 'Partially Received'],
                    vendors: ['{{vendorId}}'],
                    fetchAll: true,
                },
            },
        ],
        extractions: [avgActualLeadTimeExtraction('pos')],
        fieldBindings: [
            { fieldName: 'label', value: { type: 'literal', value: 'Average Lead Time' } },
            { fieldName: 'value', value: { type: 'ref', name: 'avgActual' } },
            {
                fieldName: 'subtext',
                value: { type: 'literal', value: 'Calculated across all supply orders' },
            },
        ],
        format: { type: 'number', suffix: ' days', decimals: 1 },
        isEditable: false,
    },

    // ── 2. Lead Time Variance (per vendor) ───────────────────────────────────
    {
        id: 'vendor-lead-time-variance',
        componentType: 'METRIC_CARD',
        apiCalls: [
            {
                id: 'pos',
                endpoint: '/purchase-orders/list',
                method: 'POST',
                params: {
                    type: 'INCOMING',
                    status: ['Received', 'Partially Received'],
                    vendors: ['{{vendorId}}'],
                    fetchAll: true,
                },
            },
        ],
        extractions: [
            avgActualLeadTimeExtraction('pos'),
            avgExpectedLeadTimeExtraction('pos'),
        ],
        fieldBindings: [
            { fieldName: 'label', value: { type: 'literal', value: 'Lead Time Variance' } },
            {
                fieldName: 'value',
                value: {
                    type: 'computed',
                    fn: 'PERCENTAGE_CHANGE',
                    args: [
                        { type: 'ref', name: 'avgActual' },
                        { type: 'ref', name: 'avgExpected' },
                    ],
                },
            },
            {
                fieldName: 'subtext',
                value: { type: 'literal', value: 'Computed across all received orders' },
            },
        ],
        format: { type: 'percentage', decimals: 1 },
        isEditable: false,
    },

    // ── 3. Fill Rate % (per vendor) ──────────────────────────────────────────
    {
        id: 'vendor-fill-rate',
        componentType: 'METRIC_CARD',
        apiCalls: [
            {
                id: 'pos',
                endpoint: '/purchase-orders/list',
                method: 'POST',
                params: {
                    type: 'INCOMING',
                    status: ['Received', 'Partially Received'],
                    vendors: ['{{vendorId}}'],
                    fetchAll: true,
                },
            },
        ],
        extractions: [
            { name: 'numerator', from: 'pos', path: 'data', field: 'actualQuantity', aggregate: 'SUM' },
            { name: 'denominator', from: 'pos', path: 'data', field: 'totalQuantity', aggregate: 'SUM' },
        ],
        fieldBindings: [
            { fieldName: 'label', value: { type: 'literal', value: 'Fill Rate' } },
            {
                fieldName: 'value',
                value: {
                    type: 'computed',
                    fn: 'MULTIPLY',
                    args: [
                        {
                            type: 'computed',
                            fn: 'DIVIDE',
                            args: [
                                { type: 'ref', name: 'numerator' },
                                { type: 'ref', name: 'denominator' },
                            ],
                        },
                        { type: 'literal', value: 100 },
                    ],
                },
            },
        ],
        format: { type: 'number', decimals: 1, suffix: '%' },
        isEditable: false,
    },

    // ── 4. On-Time Delivery % (per vendor) ───────────────────────────────────
    {
        id: 'vendor-on-time-delivery',
        componentType: 'METRIC_CARD',
        apiCalls: [
            {
                id: 'pos',
                endpoint: '/purchase-orders/list',
                method: 'POST',
                params: {
                    type: 'INCOMING',
                    status: ['Received', 'Partially Received'],
                    vendors: ['{{vendorId}}'],
                    fetchAll: true,
                },
            },
        ],
        extractions: [
            {
                // benchmarkDate = plannedDate ?? (orderDate + expectedLeadTime)
                name: 'onTimeCount',
                from: 'pos',
                path: 'data',
                perItem: {
                    fn: 'DATE_DIFF',
                    args: ['item.benchmarkDate', 'item.actualDeliveryDate'],
                },
                aggregate: 'COUNT',
            },
            {
                name: 'totalCount',
                from: 'pos',
                path: 'data',
                field: 'purchaseOrderId',
                aggregate: 'COUNT',
            },
        ],
        fieldBindings: [
            { fieldName: 'label', value: { type: 'literal', value: 'On-Time Delivery' } },
            {
                fieldName: 'value',
                value: {
                    type: 'computed',
                    fn: 'MULTIPLY',
                    args: [
                        {
                            type: 'computed',
                            fn: 'DIVIDE',
                            args: [
                                { type: 'ref', name: 'onTimeCount' },
                                { type: 'ref', name: 'totalCount' },
                            ],
                        },
                        { type: 'literal', value: 100 },
                    ],
                },
            },
        ],
        format: { type: 'number', decimals: 1, suffix: '%' },
        isEditable: false,
    },
];
